import React, { useState, useEffect } from "react";
import {
    FaBell,
    FaLink,
    FaCog,
    FaLock,
    FaGlobe,
    FaGraduationCap,
    FaCode,
    FaChartLine,
    FaEnvelope,
    FaClock,
    FaCalendarAlt,
    FaRegCommentDots,
    FaRegEdit,
    FaTimes,
    FaCookieBite,
    FaHistory,
    FaArrowRight,
    FaTasks,
    FaQuestionCircle
} from "react-icons/fa";
import Swal from "sweetalert2";
import "./Settings.css";

const Settings = () => {
    const [activeTab, setActiveTab] = useState("notifications"); // 🔹 track sidebar tab
    const [isWorkflowOn, setIsWorkflowOn] = useState(false);
    const [workflowId, setWorkflowId] = useState(null);
    const [isFormClosed, setIsFormClosed] = useState(false);

    const [isCustomMessageOn, setIsCustomMessageOn] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [closedTitle, setClosedTitle] = useState("Form closed to new submissions");
    const [closedDesc, setClosedDesc] = useState("Contact the form owner for more details.");

    const [isOpenDateOn, setIsOpenDateOn] = useState(false);
    const [isExpiryOn, setIsExpiryOn] = useState(false);
    const [isLimitOn, setIsLimitOn] = useState(false);

    const [cookieConsent, setCookieConsent] = useState(false);
    const [allowResuming, setAllowResuming] = useState(false);
    const [autoJump, setAutoJump] = useState(false);
    const [showProgress, setShowProgress] = useState(false);
    const [useExternalStorage, setUseExternalStorage] = useState(false);

    const [isFormOpenDateOn, setIsFormOpenDateOn] = useState(false);
    const [formOpenDate, setFormOpenDate] = useState("");

    const [isFormExpiryOn, setIsFormExpiryOn] = useState(false);
    const [formExpiryDate, setFormExpiryDate] = useState("");

    const [isSubmissionLimitOn, setIsSubmissionLimitOn] = useState(false);
    const [submissionLimit, setSubmissionLimit] = useState(50);

    const [respondentNotifications, setRespondentNotifications] = useState(false);

    const shouldShowCustomMessage =
        isFormClosed || isFormOpenDateOn || isFormExpiryOn || isSubmissionLimitOn;

    const getFormId = () => {
        const match = window.location.pathname.match(/form-(\d+)/);
        return match ? match[1] : null;
    };

    useEffect(() => {
        fetchWorkflows();
        fetchClosedMessage();
        fetchFormDates();
        fetchSubmissionLimit();
        fetchProgressBar();
        fetchRespondentNotifications();
    }, []);

    const fetchSubmissionLimit = async () => {
        const formId = getFormId();
        if (!formId) return;

        try {
            const res = await fetch(`/api/form_builder/forms/${formId}/submission-limit`, {
                credentials: "include",
            });
            const data = await res.json();

            if (res.ok) {
                setSubmissionLimit(data.submission_limit || 50);
                setIsSubmissionLimitOn(!!data.submission_limit);
            } else {
                console.error("Error fetching submission limit:", data.error);
            }
        } catch (err) {
            console.error("Error fetching submission limit:", err);
        }
    };

    const saveSubmissionLimit = async (limit = submissionLimit, enabled = isSubmissionLimitOn) => {
        const formId = getFormId();

        try {
            const res = await fetch("/api/form_builder/forms/update-submission-limit", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    form_id: formId,
                    submission_limit: enabled ? limit : null,
                }),
            });

            const data = await res.json();
            if (res.ok) {
                if (enabled && limit) {
                    Swal.fire("Success", `Submission limit set to ${limit}`, "success");
                } else {
                    Swal.fire("Success", "Submission limit unset", "success");
                    setIsSubmissionLimitOn(false);
                    setSubmissionLimit("");
                }
            } else {
                Swal.fire("Error", data.error || "Failed to update submission limit", "error");
            }
        } catch (error) {
            console.error("Error updating submission limit:", error);
            Swal.fire("Error", "Server error while updating submission limit", "error");
        }
    };

    const fetchFormDates = async () => {
        const formId = getFormId();
        if (!formId) return;

        try {
            const res = await fetch(`/api/form_builder/forms/${formId}/dates`, {
                credentials: "include",
            });

            const data = await res.json();

            if (res.ok) {
                if (data.form_open_date) {
                    setFormOpenDate(data.form_open_date.slice(0, 16)); // format for datetime-local
                    setIsFormOpenDateOn(true);
                }
                if (data.form_expire_date) {
                    setFormExpiryDate(data.form_expire_date.slice(0, 16));
                    setIsFormExpiryOn(true);
                }
            } else {
                console.error("Error fetching form dates:", data.error);
            }
        } catch (err) {
            console.error("Error fetching form dates:", err);
        }
    };

    const saveOpenDate = async (overrideValue = undefined) => {
        const formId = getFormId();

        // ✅ Decide the value to save
        const openDateToSave =
            overrideValue !== undefined
                ? overrideValue
                : (isFormOpenDateOn ? formOpenDate : null);

        // ✅ Validation only if we are saving a date
        if (openDateToSave) {
            const now = new Date();
            const openDate = new Date(openDateToSave);

            if (openDate < now) {
                return Swal.fire({
                    icon: "warning",
                    title: "Invalid Date",
                    text: "Form open date must be greater than the current date & time.",
                    confirmButtonColor: "#d33",
                });
            }

            if (isFormExpiryOn && formExpiryDate) {
                const expireDate = new Date(formExpiryDate);
                if (expireDate <= openDate) {
                    return Swal.fire({
                        icon: "warning",
                        title: "Invalid Dates",
                        text: "Form open date must be earlier than the expiration date.",
                        confirmButtonColor: "#d33",
                    });
                }
            }
        }

        try {
            const res = await fetch("/api/form_builder/forms/update-dates", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    form_id: formId,
                    form_open_date: openDateToSave,
                    form_expire_date: isFormExpiryOn ? formExpiryDate : null,
                }),
            });

            const data = await res.json();

            if (res.ok) {
                Swal.fire({
                    icon: "success",
                    title: "Saved",
                    text: openDateToSave
                        ? `Form Open Date saved as ${openDateToSave}`
                        : "Form Open Date unset",
                    confirmButtonColor: "#3085d6",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: data.error || "Failed to save open date",
                    confirmButtonColor: "#d33",
                });
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Something went wrong while saving open date",
            });
            console.error("Error saving open date:", err);
        }
    };

    const saveExpireDate = async (overrideValue = undefined) => {
        const formId = getFormId();

        // ✅ Decide the value to save
        const expireDateToSave =
            overrideValue !== undefined
                ? overrideValue
                : (isFormExpiryOn ? formExpiryDate : null);

        // ✅ Validation only if we are saving a date
        if (expireDateToSave) {
            const now = new Date();
            const expireDate = new Date(expireDateToSave);

            if (expireDate < now) {
                return Swal.fire({
                    icon: "warning",
                    title: "Invalid Date",
                    text: "Form expiration date must be greater than the current date & time.",
                    confirmButtonColor: "#d33",
                });
            }

            if (isFormOpenDateOn && formOpenDate) {
                const openDate = new Date(formOpenDate);
                if (expireDate <= openDate) {
                    return Swal.fire({
                        icon: "warning",
                        title: "Invalid Dates",
                        text: "Expiration date must be later than the form open date.",
                        confirmButtonColor: "#d33",
                    });
                }
            }
        }

        try {
            const res = await fetch("/api/form_builder/forms/update-dates", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    form_id: formId,
                    form_open_date: isFormOpenDateOn ? formOpenDate : null,
                    form_expire_date: expireDateToSave,
                }),
            });

            const data = await res.json();

            if (res.ok) {
                Swal.fire({
                    icon: "success",
                    title: "Saved",
                    text: expireDateToSave
                        ? `Form Expiration Date saved as ${expireDateToSave}`
                        : "Form Expiration Date unset",
                    confirmButtonColor: "#3085d6",
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: data.error || "Failed to save expiration date",
                    confirmButtonColor: "#d33",
                });
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Something went wrong while saving expiration date",
            });
            console.error("Error saving expiration date:", err);
        }
    };

    const fetchClosedMessage = async () => {
        const formId = getFormId();
        if (!formId) return;

        try {
            const res = await fetch(`/api/form_builder/forms/${formId}/close-message`, {
                credentials: "include",
            });

            const data = await res.json();

            if (res.ok) {
                setClosedTitle(data.close_title || "Form closed to new submissions");
                setClosedDesc(data.close_description || "Contact the form owner for more details.");
                setIsFormClosed(!!data.is_closed);
            } else {
                console.error("Error fetching close message:", data.error);
            }
        } catch (err) {
            console.error("Error fetching close message:", err);
        }
    };

    const handleCloseForm = async (isCurrentlyClosed) => {
        const formId = getFormId();
        const action = isCurrentlyClosed ? "re-open" : "close";

        try {
            const response = await fetch(`/api/form_builder/close-form/${formId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ is_closed: !isCurrentlyClosed }), // Toggle value
                credentials: "include",
            });

            const data = await response.json();

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: `Form ${action}ed successfully!`,
                    showConfirmButton: false,
                    timer: 1500,
                });

            } else {
                console.error(`Form ${action} failed:`, data.error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: data.error,
                });
            }
        } catch (error) {
            console.error(`Error trying to ${action} form:`, error);
            Swal.fire({
                icon: 'error',
                title: 'Server Error',
                text: `An error occurred while trying to ${action} the form.`,
            });
        }
    };

    const handleSaveClosedMessage = async () => {
        const formId = getFormId();
        if (!formId) {
            Swal.fire("Error", "Form ID not found in URL", "error");
            return;
        }

        try {
            const res = await fetch("/api/form_builder/forms/update-close-message", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    form_id: formId,
                    close_title: closedTitle,
                    close_description: closedDesc,
                }),
            });

            const data = await res.json();
            if (res.ok) {
                Swal.fire("Success", "Close page updated successfully!", "success");
                setIsModalOpen(false);
            } else {
                Swal.fire("Error", data.error || "Failed to update close page", "error");
            }
        } catch (error) {
            console.error("Error updating close page:", error);
            Swal.fire("Error", "Server error while updating close page", "error");
        }
    };

    const fetchWorkflows = async () => {
        const formId = getFormId();
        if (!formId) return;

        try {
            const res = await fetch(`/api/form_builder/workflows/${formId}`, {
                credentials: "include",
            });
            const data = await res.json();

            if (Array.isArray(data) && data.length > 0) {
                const thankYouWorkflow = data.find(
                    (wf) => wf.workflow_name === "Thank you email" && !wf.delete_at
                );

                if (thankYouWorkflow) {
                    setWorkflowId(thankYouWorkflow.id);
                    setIsWorkflowOn(true);
                } else {
                    setWorkflowId(null);
                    setIsWorkflowOn(false);
                }
            } else {
                setWorkflowId(null);
                setIsWorkflowOn(false);
            }
        } catch (err) {
            console.error("Error fetching workflows:", err);
        }
    };

    const handleCreateWorkflow = async () => {
        const formId = getFormId();
        if (!formId) {
            Swal.fire("Error", "Form ID not found in URL", "error");
            return;
        }

        try {
            const res = await fetch("/api/form_builder/workflows/create", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    form_id: formId,
                    workflow_name: "Thank you email",
                    workflow_delay_time: 30,
                }),
                credentials: "include",
            });

            const data = await res.json();
            if (res.ok) {
                Swal.fire("Success", "Workflow created successfully!", "success");
                setWorkflowId(data.id || null);
                setIsWorkflowOn(true);
            } else {
                Swal.fire("Error", data.error || "Failed to create workflow", "error");
            }
        } catch (error) {
            console.error("Error creating workflow:", error);
            Swal.fire("Error", "Server error while creating workflow", "error");
        }
    };

    const handleDeleteWorkflow = async () => {
        if (!workflowId) {
            Swal.fire("Error", "No workflow found to delete", "error");
            return;
        }

        try {
            const res = await fetch(`/api/form_builder/workflows/${workflowId}`, {
                method: "DELETE",
                credentials: "include",
            });

            const data = await res.json();
            if (res.ok) {
                Swal.fire("Deleted!", "Workflow has been deleted.", "success");
                setWorkflowId(null);
                setIsWorkflowOn(false);
            } else {
                Swal.fire("Error", data.error || "Failed to delete workflow", "error");
            }
        } catch (error) {
            console.error("Error deleting workflow:", error);
            Swal.fire("Error", "Server error while deleting workflow", "error");
        }
    };

    const handleSwitchToggle = (e) => {
        if (e.target.checked) {
            handleCreateWorkflow();
        } else {
            handleDeleteWorkflow();
        }
    };

    const fetchProgressBar = async () => {
        const formId = getFormId();
        if (!formId) return;

        try {
            const res = await fetch(`/api/form_builder/forms/${formId}/progress-bar`, {
                credentials: "include",
            });
            const data = await res.json();

            if (res.ok) {
                setShowProgress(!!data.show_progress_bar); // ✅ convert 0/1 to boolean
            } else {
                console.error("Error fetching progress bar setting:", data.error);
            }
        } catch (err) {
            console.error("Error fetching progress bar setting:", err);
        }
    };

    const handleToggleProgress = async (isCurrentlyEnabled) => {
        const formId = getFormId();
        const action = isCurrentlyEnabled ? "disable" : "enable";

        try {
            const response = await fetch(`/api/form_builder/toggle-progress/${formId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ show_progress_bar: !isCurrentlyEnabled }), // Toggle
                credentials: "include",
            });

            const data = await response.json();

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: `Progress bar ${action}d successfully!`,
                    showConfirmButton: false,
                    timer: 1500,
                });

                setShowProgress(!isCurrentlyEnabled); // update UI state
            } else {
                console.error(`Progress bar ${action} failed:`, data.error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: data.error,
                });
            }
        } catch (error) {
            console.error(`Error trying to ${action} progress bar:`, error);
            Swal.fire({
                icon: 'error',
                title: 'Server Error',
                text: `An error occurred while trying to ${action} the progress bar.`,
            });
        }
    };

    // ✅ Fetch respondent_notifications
    const fetchRespondentNotifications = async () => {
        const formId = getFormId();
        if (!formId) return;

        try {
            const res = await fetch(`/api/form_builder/forms/${formId}/respondent_notifications`, {
                credentials: "include",
            });
            const data = await res.json();

            if (res.ok) {
                setRespondentNotifications(!!data.respondent_notifications); // convert 0/1 → boolean
            } else {
                console.error("Error fetching respondent notifications:", data.error);
            }
        } catch (err) {
            console.error("Error fetching respondent notifications:", err);
        }
    };

    // ✅ Toggle respondent_notifications
    const handleToggleRespondentNotifications = async (isCurrentlyEnabled) => {
        const formId = getFormId();
        const action = isCurrentlyEnabled ? "disable" : "enable";

        try {
            const response = await fetch(`/api/form_builder/toogle-respondent_notifications/${formId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ respondent_notifications: !isCurrentlyEnabled }), // toggle
                credentials: "include",
            });

            const data = await response.json();

            if (response.ok) {
                Swal.fire({
                    icon: "success",
                    title: `Respondent notifications ${action}d successfully!`,
                    showConfirmButton: false,
                    timer: 1500,
                });

                setRespondentNotifications(!isCurrentlyEnabled); // update state
            } else {
                console.error(`Respondent notifications ${action} failed:`, data.error);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: data.error,
                });
            }
        } catch (error) {
            console.error(`Error trying to ${action} respondent notifications:`, error);
            Swal.fire({
                icon: "error",
                title: "Server Error",
                text: `An error occurred while trying to ${action} respondent notifications.`,
            });
        }
    };

    return (
        <div className="settings-container">
            {/* Sidebar */}
            <div className="settings-sidebar">
                <h2 className="settings-title">
                    <FaCog className="settings-icon" /> Settings
                </h2>
                <ul>
                    <li className={activeTab === "notifications" ? "active" : ""} onClick={() => setActiveTab("notifications")}>
                        <FaBell /> Notifications
                    </li>
                    <li><FaLink /> URL parameters</li>
                    <li
                        className={activeTab === "formBehavior" ? "active" : ""}
                        onClick={() => setActiveTab("formBehavior")}
                    >
                        <FaCog /> Form behavior
                    </li>
                    <li className={activeTab === "access" ? "active" : ""} onClick={() => setActiveTab("access")}>
                        <FaLock /> Access
                    </li>
                    <li><FaGlobe /> Language</li>
                    <li><FaGraduationCap /> Quiz mode</li>
                    <li><FaCode /> Custom code <span className="badge business">Business</span></li>
                    <li><FaChartLine /> Conversion kit <span className="badge addon">Add-on</span></li>
                </ul>
            </div>

            {/* Content Area */}
            <div className="settings-content">
                {/* Notifications Tab */}
                {activeTab === "notifications" && (
                    <>
                        <h3 className="content-title">Notifications</h3>
                        <div className="settings-tabs">
                            <span className="tab active">General</span>
                        </div>

                        <div className="notification-card">
                            <div className="notification-content">
                                <FaEnvelope className="notification-icon" />
                                <div className="notification-text">
                                    <strong>Self-email notifications</strong>
                                    <p>Get an email whenever your form is submitted</p>
                                </div>
                            </div>

                            <div className="notification-switch">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={isWorkflowOn}
                                        onChange={() => {
                                            handleSwitchToggle();
                                            setIsFormClosed(!isFormClosed);
                                        }}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>


                        {/* Respondent notifications */}
                        <div className="notification-card">
                            <div className="notification-content">
                                <FaBell className="notification-icon" />
                                <div className="notification-text">
                                    <strong>Respondent notifications</strong>
                                    <p>Send an email to the person who filled out your form upon submission</p>
                                </div>
                            </div>

                            <div className="notification-switch">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={respondentNotifications}
                                        onChange={() => handleToggleRespondentNotifications(respondentNotifications)}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>
                    </>
                )}

                {/* Access Tab */}
                {activeTab === "access" && (
                    <>
                        <h3 className="content-title">Access</h3>

                        <div className="notification-card">
                            <div className="notification-content">
                                <FaLock className="notification-icon" />
                                <div className="notification-text">
                                    <strong>Close form</strong>
                                    <p>Close your form to new submissions.</p>
                                </div>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={isFormClosed}
                                    onChange={() => {
                                        handleCloseForm(isFormClosed);
                                        setIsFormClosed(!isFormClosed);
                                    }}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>

                        {/* Form open date */}
                        <div className="notification-card">
                            <div className="notification-content">
                                <FaClock className="notification-icon" />
                                <div className="notification-text">
                                    <strong>Form open date</strong>
                                    <p>Set a date for your form to become available.</p>

                                    {isFormOpenDateOn && (
                                        <div style={{ marginTop: "8px", display: "flex", gap: "8px", alignItems: "center" }}>
                                            <input
                                                type="datetime-local"
                                                value={formOpenDate}
                                                onChange={(e) => setFormOpenDate(e.target.value)}
                                            />
                                            <button className="save-btn" onClick={() => saveOpenDate()}>
                                                Save
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="notification-switch">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={isFormOpenDateOn}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setIsFormOpenDateOn(checked);

                                            if (!checked) {
                                                setFormOpenDate("");
                                                saveOpenDate(null);
                                            }
                                        }}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>

                        {/* Form expiration date */}
                        <div className="notification-card">
                            <div className="notification-content">
                                <FaCalendarAlt className="notification-icon" />
                                <div className="notification-text">
                                    <strong>Form expiration date</strong>
                                    <p>Close form upon reaching a certain date.</p>

                                    {isFormExpiryOn && (
                                        <div style={{ marginTop: "8px", display: "flex", gap: "8px", alignItems: "center" }}>
                                            <input
                                                type="datetime-local"
                                                value={formExpiryDate}
                                                onChange={(e) => setFormExpiryDate(e.target.value)}
                                            />
                                            <button className="save-btn" onClick={() => saveExpireDate()} >
                                                Save
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="notification-switch">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={isFormExpiryOn}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setIsFormExpiryOn(checked);

                                            if (!checked) {
                                                setFormExpiryDate("");
                                                saveExpireDate(null);
                                            }
                                        }}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>

                        {/* Form submission limit */}
                        <div className="notification-card">
                            <div className="notification-content">
                                <FaChartLine className="notification-icon" />
                                <div className="notification-text">
                                    <strong>Form submission limit</strong>
                                    <p>Close form after reaching a certain number of submissions.</p>

                                    {isSubmissionLimitOn && (
                                        <div style={{ marginTop: "8px", display: "flex", gap: "8px", alignItems: "center" }}>
                                            <input
                                                type="number"
                                                min="1"
                                                value={submissionLimit}
                                                onChange={(e) => setSubmissionLimit(e.target.value)}
                                            />
                                            <button
                                                className="save-btn"
                                                onClick={saveSubmissionLimit}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="notification-switch">
                                <label className="switch">
                                    <input
                                        type="checkbox"
                                        checked={isSubmissionLimitOn}
                                        onChange={(e) => {
                                            const checked = e.target.checked;

                                            if (!checked) {
                                                setIsSubmissionLimitOn(false);
                                                setSubmissionLimit(null);
                                                saveSubmissionLimit(null, false);
                                            } else {
                                                setIsSubmissionLimitOn(true);
                                            }
                                        }}
                                    />
                                    <span className="slider round"></span>
                                </label>
                            </div>
                        </div>

                        {shouldShowCustomMessage && (
                            <div className="notification-card">
                                <div className="notification-content">
                                    <FaRegCommentDots className="notification-icon" />
                                    <div className="notification-text">
                                        <strong>Custom form closed message</strong>
                                        <p>Provide custom messages to show users when the form is closed/expired.</p>

                                        {isCustomMessageOn && (
                                            <button className="edit-close-btn" onClick={() => setIsModalOpen(true)}>
                                                <FaRegEdit style={{ color: "rgb(107 114 128)", fontSize: "1.1rem" }} /> Edit close page
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="notification-switch">
                                    <label className="switch">
                                        <input
                                            type="checkbox"
                                            checked={isCustomMessageOn}
                                            onChange={(e) => setIsCustomMessageOn(e.target.checked)}
                                        />
                                        <span className="slider round"></span>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* Modal */}
                        {isModalOpen && (
                            <div className="custom-modal-overlay">
                                <div className="custom-modal">
                                    <div className="custom-modal-header">
                                        <h3>Edit close page</h3>
                                        <button
                                            className="custom-close-icon"
                                            onClick={() => setIsModalOpen(false)}
                                        >
                                            <FaTimes style={{ color: "rgb(156 163 175)", fontSize: "1.2rem" }} />
                                        </button>
                                    </div>

                                    <div className="custom-closed-wrapper">
                                        <div className="custom-closed-card">
                                            <FaLock
                                                size={48}
                                                style={{ marginBottom: "1rem", color: "rgb(155, 160, 168)" }}
                                            />

                                            {/* Editable Title */}
                                            <input
                                                type="text"
                                                className="closed-input"
                                                value={closedTitle}
                                                onChange={(e) => setClosedTitle(e.target.value)}
                                            />

                                            {/* Editable Description */}
                                            <textarea
                                                className="closed-textarea"
                                                value={closedDesc}
                                                onChange={(e) => setClosedDesc(e.target.value)}
                                                rows="3"
                                            />
                                        </div>
                                    </div>

                                    <div className="modal-actions">
                                        <button onClick={handleSaveClosedMessage} className="save-btn">
                                            Done
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                    </>
                )}

                {/* Form Behavior Tab */}
                {activeTab === "formBehavior" && (
                    <>
                        <h3 className="content-title">Form behavior</h3>
                        <p className="content-subtitle">
                            Global settings to control how your form behaves when used
                        </p>

                        {/* Cookie consent */}
                        <div className="notification-card">
                            <div className="notification-content">
                                <FaCookieBite className="notification-icon" />
                                <div className="notification-text">
                                    <strong>
                                        Cookie consent
                                        <span className="tooltip-wrapper" style={{ marginLeft: "10px" }}>
                                            <FaQuestionCircle className="question-icon" />
                                            <span className="tooltip-text">
                                                Show a cookie consent message on your live forms.
                                                We recommend enabling this setting to meet applicable legal requirements.
                                            </span>
                                        </span>
                                    </strong>
                                </div>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={cookieConsent}
                                    disabled
                                    onChange={() => setCookieConsent(!cookieConsent)}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>

                        {/* Allow resuming partial submissions */}
                        <div className="notification-card">
                            <div className="notification-content">
                                <FaHistory className="notification-icon" />
                                <div className="notification-text">
                                    <strong>Allow resuming partial submissions
                                        <span className="tooltip-wrapper" style={{ marginLeft: "10px" }}>
                                            <FaQuestionCircle className="question-icon" />
                                            <span className="tooltip-text">
                                                If enabled, users can come back to the
                                                form they previously started and
                                                resume where they left off.
                                            </span>
                                        </span>
                                    </strong>
                                </div>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={true}
                                    disabled
                                    onChange={() => setAllowResuming(!allowResuming)}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>

                        {/* Auto-jump */}
                        <div className="notification-card">
                            <div className="notification-content">
                                <FaArrowRight className="notification-icon" />
                                <div className="notification-text">
                                    <strong>
                                        Auto-jump to the next page
                                        <span className="tooltip-wrapper">
                                            <FaQuestionCircle className="question-icon" />
                                            <span className="tooltip-text">
                                                Automatically transition to the next page when a user answers
                                                a multiple choice, picture choice, dropdown, star rating, or
                                                opinion scale question. <br /><br />
                                                Only applies when there is a single question on the page and
                                                when the form is not about to be completed.
                                            </span>
                                        </span>
                                    </strong>
                                </div>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={autoJump}
                                    disabled
                                    onChange={() => setAutoJump(!autoJump)}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>

                        {/* Show progress bar */}
                        <div className="notification-card">
                            <div className="notification-content">
                                <FaTasks className="notification-icon" />
                                <div className="notification-text">
                                    <strong>Show progress bar</strong>
                                </div>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={showProgress}
                                    onChange={() => handleToggleProgress(showProgress)}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>

                        {/* Only use of external storage */}
                        <div className="notification-card">
                            <div className="notification-content">
                                <FaTasks className="notification-icon" />
                                <div className="notification-text">
                                    <strong>
                                        Only use external storage
                                        <span className="tooltip-wrapper">
                                            <FaQuestionCircle className="question-icon" />
                                            <span className="tooltip-text">
                                                If enabled, the system will not store form results and will instead
                                                only pass collected data to any connected integrations. <br /><br />
                                                File upload fields are still stored on AWS S3 as many 3rd-party
                                                integrations require an external link to uploaded files.
                                            </span>
                                        </span>
                                    </strong>
                                </div>
                            </div>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={useExternalStorage}
                                    disabled
                                    onChange={() => setUseExternalStorage(!useExternalStorage)}
                                />
                                <span className="slider round"></span>
                            </label>
                        </div>
                    </>
                )}

            </div>
        </div>
    );
};

export default Settings;
